import { Stack, Typography, useTheme } from "@mui/material";
import {
	type Control,
	type FieldValues,
	type Path,
	useWatch,
} from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type PensionAdditionalFieldsProps<
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, TContext, TTransformed>;
};

export const PensionAdditionalFields = <
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
>({
	control,
}: PensionAdditionalFieldsProps<FormType, TContext, TTransformed>) => {
	const theme = useTheme();
	const { t } = useResource();
	const signedAt = useWatch({
		control,
		name: "signedAt" as Path<FormType>,
	}) as Date | null;

	return (
		<>
			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddPension.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name={"signedAt" as Path<FormType>}
					label={t(Resources.Portfolio.AddPension.Fields.signedAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name={"startAt" as Path<FormType>}
					label={t(Resources.Portfolio.AddPension.Fields.startAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name={"endAt" as Path<FormType>}
					label={t(Resources.Portfolio.AddPension.Fields.endAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					minDate={signedAt ?? undefined}
					isLast
				/>
			</Stack>
		</>
	);
};
